<template>
  <div>
    <!-- TOP BAR -->
    <div class="settings-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <p class="d-inline-flex">
          <a v-if="!isNew()" class="btn btn-danger btn-icon btn btn mar-rt-10 delete" @click.prevent="handleDelete">
            <img src="/static/img/icons/icon-close.png">
            {{$t('general.delete')}}
          </a>
          <button type="submit" class="btn btn-success btn-icon mar-rt-10 save" @click="validate" :title="$t('general.save')">
            <img src="/static/img/icons/save.png">
            {{$t('general.save')}}
          </button>
        </p>
      </div>
    </div>

    <!-- LOADING -->
    <div :class="(loading ? 'show-loader' : 'hide-loader')">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <!-- FORM -->
    <form class="row pad-to-15" @submit.prevent="validate">
      <div class="col-md-12" v-if="errors.items.length">
        <!-- <div class="alert alert-danger alert-custom-danger">{{$t('validations.checkErrors')}}</div> -->
      </div>

      <div class="col-md-6">
        <div class="card">
          <div class="card-content">
            <!-- NUME -->
            <div class="form-group">
              <label>{{$t('kds.name')}} *</label>
              <input 
                type="text"
                name="name"
                :placeholder="$t('kds.name')"
                v-validate="'required|min:3'"
                v-model="model.name"
                class="form-control"
              >
              <small class="text-danger" v-if="errors.has('name')">
                {{ errors.first('name') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card">
          <div class="card-content">
            <!-- LOCAȚII -->
            <div class="form-group">
              <label>Selectează locații *</label>
              <small class="help-block text-muted">
                Pe aceeași locație poate exista un singur profil fără alte filtre.
                Al doilea profil pe aceeași locație trebuie să aibă în plus o categorie de top,
                o zonă de livrare sau un grup de clienți.
              </small>
              <v-select
                v-model="model.location_ids"
                :options="availableLocations"
                multiple
                label="name"
                :reduce="loc => loc.true_id"
                :placeholder="$t('general.select')"
                v-validate="'required'"
                name="location_ids"
                data-vv-as="locații"
                @input="onLocationsChanged"
              />
              <small class="text-danger" v-if="errors.has('location_ids')">
                {{ errors.first('location_ids') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- FILTRE PROFIL: GRUPURI DE CLIENȚI / ZONE DE LIVRARE / CATEGORII DE TOP -->
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4>Filtre profil</h4>
            <small class="text-muted">
              Opțional. La aplicare prioritatea este: grup de clienți &gt; zonă de livrare &gt; categorie de top.
              Dacă profilul are mai multe filtre setate, trebuie să se potrivească toate simultan.
              Aceeași categorie/zonă/grup nu poate exista în două profile pe aceeași locație.
            </small>
          </div>
          <div class="card-content">
            <div class="alert alert-warning" v-if="plainProfileTaken && !hasAnyScope">
              Există deja un profil doar pe locație pentru locațiile selectate:
              <strong>{{ plainProfileTaken }}</strong>. Alege cel puțin un filtru mai jos.
            </div>

            <div v-if="!selectedLocationIds.length" class="text-muted">
              Selectează întâi locațiile pentru a putea alege filtrele.
            </div>

            <div class="row" v-else>
              <div class="col-md-4">
                <div class="form-group">
                  <label>Grupuri de clienți</label>
                  <v-select
                    v-model="model.customers_group_ids"
                    :options="scopeOptions.customers_group"
                    multiple
                    label="name"
                    :placeholder="$t('general.select')"
                    :disabled="loadingScopeOptions"
                  />
                  <small class="help-block text-muted" v-if="takenLabel('customers_group_ids')">
                    Ascunse (deja folosite în alt profil): {{ takenLabel('customers_group_ids') }}
                  </small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group">
                  <label>Zone de livrare</label>
                  <v-select
                    v-model="model.delivery_range_ids"
                    :options="scopeOptions.delivery_ranges"
                    multiple
                    label="name"
                    :placeholder="$t('general.select')"
                    :disabled="loadingScopeOptions"
                  />
                  <small class="help-block text-muted" v-if="takenLabel('delivery_range_ids')">
                    Ascunse (deja folosite în alt profil): {{ takenLabel('delivery_range_ids') }}
                  </small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group">
                  <label>Categorii de top</label>
                  <v-select
                    v-model="model.top_category_ids"
                    :options="scopeOptions.top_categories"
                    multiple
                    label="name"
                    :placeholder="$t('general.select')"
                    :disabled="loadingScopeOptions"
                  />
                  <small class="help-block text-muted" v-if="takenLabel('top_category_ids')">
                    Ascunse (deja folosite în alt profil): {{ takenLabel('top_category_ids') }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TABEL PREȚURI PRODUSE -->
      <div class="col-md-12" v-if="!isNew()">
        <div class="card pricing-table-card">
          <div class="card-header">
            <h4>Configurare Prețuri Produse</h4>
            <small class="text-muted">Setează prețurile pentru fiecare produs în funcție de sursă și metodă</small>
          </div>
          <div class="card-content">

            <!-- FILTRE -->
            <div class="filters-wrapper">
              <div class="row">
                <div class="col-md-3">
                  <div class="form-group">
                    <label>Caută după nume</label>
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="filters.name"
                      placeholder="Nume produs..."
                      @input="debouncedLoadProducts"
                    >
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group">
                    <label>Caută după ID</label>
                    <input 
                      type="number" 
                      class="form-control"
                      v-model="filters.product_id"
                      placeholder="ID produs..."
                      @input="debouncedLoadProducts"
                    >
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>{{$t('catalogProd.category')}}</label>
                    <select 
                      class="form-control"
                      v-model="filters.category_id"
                      @change="loadProducts"
                    >
                      <option value="">Toate categoriile</option>
                      <option 
                        v-for="option in mainCategories"
                        :value="option.id"
                        :key="option.id"
                      >
                        {{ option.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group">
                    <label>&nbsp;</label>
                    <button 
                      type="button" 
                      class="btn btn-default btn-block"
                      @click="resetFilters"
                    >
                      <i class="ti-close"></i> Reset
                    </button>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      class="btn btn-block"
                      :class="discountSelectionCount ? 'btn-info' : 'btn-default'"
                      @click="openDiscountCampaignModal"
                    >
                      <i class="ti-tag"></i>
                      {{ $t('discountCampaigns.campaignButton') }}
                      <span v-if="discountSelectionCount">({{ discountSelectionCount }})</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Selection bar -->
            <div class="discount-selection-bar" v-if="discountSelectionCount">
              <div class="discount-selection-bar__info">
                {{ $t('discountCampaigns.selectionSummary', {
                  cells: discountSelectionCount,
                  products: discountSelectionProductCount
                }) }}
              </div>
              <div class="discount-selection-bar__actions">
                <button type="button" class="btn btn-sm btn-info" @click="openDiscountCampaignModal">
                  {{ $t('discountCampaigns.applyDiscount') }}
                </button>
                <button type="button" class="btn btn-sm btn-default" @click="clearDiscountSelection">
                  {{ $t('discountCampaigns.clearSelection') }}
                </button>
              </div>
            </div>

            <!-- Loading pentru produse -->
            <div v-if="loadingProducts" class="text-center py-3">
              <i class="ti-reload rotate"></i> Se încarcă produsele...
            </div>

            <div v-else class="table-responsive-wrapper">
              <!-- Container doar pentru scroll orizontal -->
              <div class="table-horizontal-scroll">
                <table class="table pricing-table">
                  <thead>
                    <tr>
                      <!-- Coloana Produs (sticky) -->
                      <th class="product-col sticky-col">Produs (Preț Standard)</th>

                      <!-- Surse cu metode nested -->
                      <template v-for="source in sources">
                        <th 
                          v-for="method in source.methods" 
                          :key="`${source.id}-${method.id}`"
                          class="source-method-col"
                        >
                          <div class="source-header">{{ source.name }}</div>
                          <div class="method-header">{{ method.name }}</div>
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="product in products" :key="product.id">
                      <!-- Info Produs (sticky) -->
                      <td class="product-col sticky-col">
                        <div class="product-info">
                          <strong @click="goToProduct(product)">{{ product.name }}</strong>
                          <div class="product-id text-muted">ID: {{ product.id }}</div>
                          <div class="standard-price text-muted">
                            Preț: {{ product.price }} RON
                          </div>
                          <!-- BUTON APLICARE RAPIDĂ -->
                          <button 
                            type="button" 
                            class="btn btn-sm btn-info btn-block quick-apply-btn"
                            @click="openQuickApplyModal(product)"
                          >
                            <i class="ti-light-bulb"></i> Aplicare rapidă
                          </button>
                        </div>
                      </td>

                      <!-- Celule pentru fiecare sursă+metodă -->
                      <template v-for="source in sources">
                        <td 
                          v-for="method in source.methods" 
                          :key="`${product.id}-${source.id}-${method.id}`"
                          class="pricing-cell"
                        >
                          <div class="field-row checkbox-row">
                            <label class="checkbox-label">
                              <input 
                                type="checkbox" 
                                :checked="getEditMode(product.id, source.id, method.id)"
                                @change="toggleEditMode(product.id, source.id, method.id, $event.target.checked)"
                              >
                              <span>{{ $t('general.edit') }}</span>
                            </label>
                          </div>

                          <div v-if="pricingData[`${product.id}_${source.id}_${method.id}`]"
                           class="pricing-fields" :class="{ 'disabled': !getEditMode(product.id, source.id, method.id) }">
                            <!-- Preț -->
                            <div class="field-row">
                              <label>Preț</label>
                              <input 
                                type="number" 
                                step="0.01"
                                class="form-control input-sm"
                                v-model="pricingData[`${product.id}_${source.id}_${method.id}`].price"
                                placeholder="0.00"
                              >
                            </div>

                            <!-- TVA Select -->
                            <div class="field-row">
                              <label>TVA</label>
                              <select
                                @change="updateVatFromSelect(product.id, source.id, method.id, $event)"
                                class="form-control input-sm"
                              >
                                <option :value="null">Selectează TVA</option>
                                <option
                                  v-for="vat in vatOptions"
                                  :key="vat.id"
                                  :value="vat.id"
                                  :data-amount="vat.amount"
                                  :selected="pricingData[`${product.id}_${source.id}_${method.id}`].vat_id == vat.id"
                                >
                                  {{ vat.amount }}% <template v-if="vat.tax && vat.tax.name">- {{vat.tax.name}}</template>
                                </option>
                                <option 
                                  style="display: none" 
                                  :value="pricingData[`${product.id}_${source.id}_${method.id}`].vat_id"
                                  v-if="!vatOptions.find(v => v.id == pricingData[`${product.id}_${source.id}_${method.id}`].vat_id) && pricingData[`${product.id}_${source.id}_${method.id}`].vat_id"
                                >
                                  {{ pricingData[`${product.id}_${source.id}_${method.id}`].vat_amount }}%
                                </option>
                              </select>
                            </div>

                            <!-- POS ID -->
                            <div class="field-row">
                              <label>POS ID</label>
                              <input 
                                type="text" 
                                class="form-control input-sm"
                                v-model="pricingData[`${product.id}_${source.id}_${method.id}`].pos_id"
                                placeholder="ID POS"
                              >
                            </div>

                            <!-- Active -->
                            <div class="field-row checkbox-row">
                              <label class="checkbox-label">
                                <input 
                                  type="checkbox" 
                                  v-model="pricingData[`${product.id}_${source.id}_${method.id}`].active"
                                >
                                <span>Activ</span>
                              </label>
                            </div>
                          </div>

                          <!-- Compact discount row -->
                          <div
                            class="discount-row-compact"
                            :class="{
                              'is-selected': isDiscountCellSelected(product.id, source.id, method.id),
                              'has-discount': hasDiscounts(product.id, source.id, method.id)
                            }"
                          >
                            <input
                              type="checkbox"
                              class="discount-select-check"
                              :checked="isDiscountCellSelected(product.id, source.id, method.id)"
                              :disabled="!pricingData[`${product.id}_${source.id}_${method.id}`]"
                              :title="pricingData[`${product.id}_${source.id}_${method.id}`]
                                ? $t('discountCampaigns.selectForCampaign')
                                : $t('discountCampaigns.noPriceConfigured')"
                              @change="toggleDiscountCellSelection(product, source, method, $event.target.checked)"
                            >
                            <span
                              v-if="hasDiscounts(product.id, source.id, method.id)"
                              class="discount-dot"
                              :class="{ 'is-inactive': !isDiscountCampaignActive(product.id, source.id, method.id) }"
                            ></span>
                            <template v-if="hasDiscounts(product.id, source.id, method.id)">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                class="form-control input-sm discount-input-compact"
                                :class="{ 'is-saved-flash': discountFlashKey === getPricingKey(product.id, source.id, method.id) }"
                                :value="getDiscountSum(product.id, source.id, method.id)"
                                :readonly="getDiscounts(product.id, source.id, method.id).length > 1"
                                :title="getDiscountTooltip(product.id, source.id, method.id)"
                                @change="onDiscountSumChange(product.id, source.id, method.id, $event)"
                                @click="onDiscountInputClick(product, source, method)"
                              >
                              <sup
                                v-if="getDiscounts(product.id, source.id, method.id).length > 1"
                                class="discount-count"
                              >{{ getDiscounts(product.id, source.id, method.id).length }}</sup>
                              <button
                                type="button"
                                class="btn btn-xs btn-default discount-edit-btn"
                                :title="$t('discountCampaigns.viewEditDiscount')"
                                @click="openDiscountLineModal(product, source, method)"
                              >
                                <i class="ti-pencil"></i>
                              </button>
                            </template>
                            <span v-else class="discount-empty text-muted">
                              {{ $t('discountCampaigns.discShort') }} —
                            </span>
                          </div>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Paginare -->
            <div class="pagination-wrapper" v-if="productsPagination.totalPages > 1">
              <div class="pagination-info">
                Pagina {{ productsPagination.page }} din {{ productsPagination.totalPages }}
                ({{ productsPagination.totalRecords }} produse)
              </div>
              <div class="pagination-controls">
                <button 
                  class="btn btn-sm btn-default" 
                  :disabled="productsPagination.page === 1"
                  @click.prevent="changePage(productsPagination.page - 1)"
                >
                  <i class="ti-arrow-left"></i> Anterior
                </button>
                <button 
                  class="btn btn-sm btn-default" 
                  :disabled="productsPagination.page === productsPagination.totalPages"
                  @click.prevent="changePage(productsPagination.page + 1)"
                >
                  Următor <i class="ti-arrow-right"></i>
                </button>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="!loadingProducts && products.length === 0" class="text-center py-4 text-muted">
              <i class="ti-package" style="font-size: 48px;"></i>
              <p>Nu există produse disponibile pentru configurare</p>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- MODAL APLICARE RAPIDĂ -->
    <div v-if="showQuickApplyModal" class="modal-overlay" @click.self="closeQuickApplyModal">
      <div class="modal-dialog quick-apply-modal">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">
              <i class="ti-light-bulb"></i> Aplicare rapidă
            </h4>
            <p class="modal-subtitle" v-if="quickApplyProduct">
              <strong>{{ quickApplyProduct.name }}</strong> (ID: {{ quickApplyProduct.id }})
            </p>
            <button type="button" class="close" @click="closeQuickApplyModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="modal-body">
            <p class="modal-description">
              Completează valorile de mai jos pentru a le aplica la <strong>toate sursele și metodele</strong> acestui produs.
            </p>

            <div class="quick-apply-form">
              <!-- ȘTERGERE DIN LISTA DE PREȚURI -->
              <div class="form-group">
                <button
                  type="button"
                  class="btn btn-danger btn-block quick-clear-btn"
                  @click="clearProductPricing"
                >
                  <i class="ti-trash"></i> Șterge din lista de prețuri
                </button>
                <small class="help-block text-muted">
                  Dezactivează toate sursele și metodele acestui produs. Recordurile existente vor fi șterse la salvare.
                </small>
              </div>

              <!-- Preț -->
              <div class="form-group">
                <label>Preț</label>
                <input 
                  type="number" 
                  step="0.01"
                  class="form-control"
                  v-model="quickApplyData.price"
                  placeholder="0.00"
                >
              </div>

              <!-- TVA Select -->
              <div class="form-group">
                <label>TVA</label>
                <select
                  v-model="quickApplyData.vat_id"
                  @change="updateQuickApplyVat"
                  class="form-control"
                >
                  <option :value="null">Selectează TVA</option>
                  <option
                    v-for="vat in vatOptions"
                    :key="vat.id"
                    :value="vat.id"
                  >
                    {{ vat.amount }}% <template v-if="vat.tax && vat.tax.name">- {{vat.tax.name}}</template>
                  </option>
                </select>
              </div>

              <!-- POS ID -->
              <div class="form-group">
                <label>POS ID</label>
                <input 
                  type="text" 
                  class="form-control"
                  v-model="quickApplyData.pos_id"
                  placeholder="ID POS"
                >
              </div>

              <!-- Active -->
              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    v-model="quickApplyData.active"
                  >
                  <span>Activ</span>
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer pp-modal-footer">
            <p class="d-inline-flex pp-modal-footer__actions">
              <a href="#" class="btn btn-danger btn-icon mar-rt-10 delete" @click.prevent="closeQuickApplyModal">
                <img src="/static/img/icons/icon-close.png">
                Anulează
              </a>
              <button type="button" class="btn btn-success btn-icon save" @click="applyQuickApply">
                <img src="/static/img/icons/save.png">
                Aplică la toate
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CREATE DISCOUNT CAMPAIGN -->
    <div v-if="showDiscountCampaignModal" class="modal-overlay" @click.self="closeDiscountCampaignModal">
      <div class="modal-dialog discount-campaign-modal">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">
              <i class="ti-tag"></i> {{ $t('discountCampaigns.newCampaignModal') }}
            </h4>
            <p class="modal-subtitle">
              {{ $t('discountCampaigns.newCampaignSubtitle', {
                name: model.name || '',
                cells: discountSelectionCount
              }) }}
            </p>
            <button type="button" class="close" @click="closeDiscountCampaignModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="modal-body discount-campaign-modal__body">
            <discount-campaign-form
              v-if="showDiscountCampaignModal"
              ref="discountCampaignForm"
              :embedded="true"
              :hide-preview="true"
              :lock-price-profile="true"
              :prefill="discountCampaignPrefill"
              :route="{ params: {} }"
              @saved="onDiscountCampaignSaved"
              @cancel="closeDiscountCampaignModal"
            />
          </div>
          <div class="modal-footer pp-modal-footer">
            <p class="d-inline-flex pp-modal-footer__actions">
              <a href="#" class="btn btn-danger btn-icon mar-rt-10 delete" @click.prevent="closeDiscountCampaignModal">
                <img src="/static/img/icons/icon-close.png">
                {{ $t('general.cancel') }}
              </a>
              <button type="button" class="btn btn-success btn-icon save" @click="submitDiscountCampaignModal">
                <img src="/static/img/icons/save.png">
                {{ $t('discountCampaigns.createCampaign') }}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL EDIT / DELETE DISCOUNT LINE -->
    <div v-if="showDiscountLineModal" class="modal-overlay" @click.self="closeDiscountLineModal">
      <div class="modal-dialog discount-line-modal">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">
              <i class="ti-tag"></i> {{ $t('discountCampaigns.discount') }}
            </h4>
            <p class="modal-subtitle" v-if="discountLineContext">
              <strong>{{ discountLineContext.productName }}</strong>
              #{{ discountLineContext.productId }}
              · {{ discountLineContext.sourceName }} {{ discountLineContext.methodName }}
            </p>
            <button type="button" class="close" @click="closeDiscountLineModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="modal-body" v-if="discountLineContext">
            <div
              class="discount-line-editor"
              v-for="(lineEdit, idx) in discountLineEdits"
              :key="lineEdit.id"
            >
              <div class="discount-line-editor__meta">
                <div class="meta-row">
                  <span class="meta-label">{{ $t('discountCampaigns.single') }}</span>
                  <span class="meta-value">
                    {{ lineEdit.campaignName || ('#' + lineEdit.campaignId) }}
                  </span>
                </div>
                <div class="meta-row" v-if="lineEdit.period">
                  <span class="meta-label">{{ $t('discountCampaigns.period') }}</span>
                  <span class="meta-value">{{ lineEdit.period }}</span>
                </div>
                <div class="meta-row" v-if="lineEdit.number_of_units != null">
                  <span class="meta-label">{{ $t('discountCampaigns.cap') }}</span>
                  <span class="meta-value">
                    {{ lineEdit.number_of_units }} · {{ $t('discountCampaigns.unitsSold') }} {{ lineEdit.units_sold || 0 }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label>{{ $t('discountCampaigns.listPrice') }}</label>
                <input type="text" class="form-control" :value="lineEdit.basePrice" readonly>
              </div>
              <div class="form-group">
                <label>{{ $t('discountCampaigns.discount') }}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  v-model.number="lineEdit.discount"
                  @input="recalcLineEdit(idx)"
                >
                <small class="text-danger" v-if="lineEdit.error">{{ lineEdit.error }}</small>
              </div>
              <div class="form-group">
                <label>{{ $t('discountCampaigns.discountedPrice') }}</label>
                <input type="text" class="form-control" :value="lineEdit.discounted_price" readonly>
              </div>
              <hr v-if="idx < discountLineEdits.length - 1">
            </div>
          </div>
          <div class="modal-footer pp-modal-footer pp-modal-footer--split" v-if="discountLineEdits.length">
            <p class="d-inline-flex">
              <a
                href="#"
                class="btn btn-danger btn-icon delete"
                :class="{ disabled: discountLineSaving }"
                @click.prevent="!discountLineSaving && deleteDiscountLinesFromModal()"
              >
                <img src="/static/img/icons/icon-close.png">
                {{ $t('general.delete') }}
              </a>
            </p>
            <p class="d-inline-flex pp-modal-footer__actions">
              <a href="#" class="btn btn-danger btn-icon mar-rt-10 delete" @click.prevent="closeDiscountLineModal">
                <img src="/static/img/icons/icon-close.png">
                {{ $t('general.cancel') }}
              </a>
              <button
                type="button"
                class="btn btn-success btn-icon save"
                :disabled="discountLineSaving || hasDiscountLineErrors"
                @click="saveAllDiscountLinesFromModal"
              >
                <img src="/static/img/icons/save.png">
                {{ $t('general.save') }}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue'
import vSelect from 'vue-select'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'
import DiscountCampaignForm from 'src/components/Dashboard/Views/Marketing/DiscountCampaigns/DiscountCampaignForm.vue'

Vue.component('v-select', vSelect)

export default {
  components: {
    DiscountCampaignForm
  },
  props: {
    priceProfileData: {
      type: Object,
      default: () => ({})
    },
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: true,
      loadingProducts: false,
      model: {
        name: '',
        location_ids: [],
        top_category_ids: [],
        delivery_range_ids: [],
        customers_group_ids: []
      },
      availableLocations: [],

      scopeFields: ['customers_group_ids', 'delivery_range_ids', 'top_category_ids'],

      // Filtrele profilului, filtrate pe locațiile selectate
      loadingScopeOptions: false,
      scopeOptions: {
        top_categories: [],
        delivery_ranges: [],
        customers_group: []
      },
      // aceleași liste, nefiltrate — ca să avem numele și pentru cele ascunse
      scopeAllOptions: {
        top_categories: [],
        delivery_ranges: [],
        customers_group: []
      },
      // entitățile deja folosite de alt profil pe aceleași locații (id -> nume profil)
      scopeTaken: {
        top_category_ids: {},
        delivery_range_ids: {},
        customers_group_ids: {}
      },
      plainProfileTaken: null,

      companyId: this.$session.get('selectedCompany'),
      token: this.$session.get('token'),

      filters: {
        name: '',
        product_id: '',
        category_id: ''
      },
      debounceTimer: null,
      mainCategories: [],

      sources: [
        //TSK-1443
        {
          id: 6, 
          name: 'GLOVO', 
          methods: [
            { id: 1, name: 'EatIn' }
          ] 
        },
        {
          id: 7, 
          name: 'BOLT', 
          methods: [
            { id: 1, name: 'EatIn' }
          ] 
        },
        {
          id: 8, 
          name: 'Wolt', 
          methods: [
            { id: 1, name: 'EatIn' }
          ] 
        },
        {
          id: 1, 
          name: 'Website', 
          methods: [
            { id: 1, name: 'EatIn' },
            { id: 2, name: 'ToGo' },
            { id: 3, name: 'Delivery' }
          ] 
        },
        {
          id: 2, 
          name: 'QR', 
          methods: [
            { id: 1, name: 'EatIn' }
          ] 
        },
        {
          id: 3, 
          name: 'Kiosk', 
          methods: [
            { id: 1, name: 'EatIn' },
            { id: 2, name: 'ToGo' }
          ] 
        },
        {
          id: 4, 
          name: 'POS', 
          methods: [
            { id: 1, name: 'EatIn' },
            { id: 2, name: 'ToGo' },
            { id: 3, name: 'Delivery' }
          ] 
        },
        {
          id: 5, 
          name: 'App', 
          methods: [
            { id: 1, name: 'EatIn' },
            { id: 2, name: 'ToGo' },
            { id: 3, name: 'Delivery' }
          ] 
        }
      ],
      products: [],
      productsPagination: {
        page: 1,
        perPage: 20,
        totalPages: 0,
        totalRecords: 0
      },
      // Separat: pricing data de la API pentru produsele din pagina curenta
      apiPricingData: {},
      pricingData: {},
      editModes: {},
      vatOptions: [],

      // Modal aplicare rapidă
      showQuickApplyModal: false,
      quickApplyProduct: null,
      quickApplyData: {
        price: '',
        vat_id: null,
        vat_amount: '',
        pos_id: '',
        active: true
      },

      // Discount campaign selection + modals
      discountSelection: {},
      showDiscountCampaignModal: false,
      discountCampaignPrefill: null,
      showDiscountLineModal: false,
      discountLineContext: null,
      discountLineEdits: [],
      discountLineSaving: false,
      discountFlashKey: null,
      discountFlashTimer: null
    }
  },
  computed: {
    discountSelectionCount () {
      return Object.keys(this.discountSelection).length
    },
    discountSelectionProductCount () {
      const ids = new Set(Object.values(this.discountSelection).map(s => s.product_id))
      return ids.size
    },
    hasDiscountLineErrors () {
      return this.discountLineEdits.some(line => !!line.error)
    },
    // v-select 2.x păstrează obiectele întregi; acceptăm și id-uri simple ca fallback
    selectedLocationIds () {
      return (this.model.location_ids || [])
        .map(loc => (loc && typeof loc === 'object') ? loc.true_id : loc)
        .filter(id => id !== null && id !== undefined && id !== '')
    },
    hasAnyScope () {
      return this.scopeFields.some(field => (this.model[field] || []).length > 0)
    }
  },
  created () {
    if (!this.guardPriceListsAccess()) {
      return
    }
    this.getData()
    this.getVatOptions()
    this.getCategories()
  },
  beforeDestroy () {
    clearTimeout(this.discountFlashTimer)
  },
  methods: {
    // maparea câmp din payload -> cheia listei de opțiuni întoarsă de API
    scopeOptionKey (field) {
      return {
        top_category_ids: 'top_categories',
        delivery_range_ids: 'delivery_ranges',
        customers_group_ids: 'customers_group'
      }[field]
    },

    scopeIds (field) {
      return (this.model[field] || [])
        .map(item => (item && typeof item === 'object') ? item.id : item)
        .filter(id => id !== null && id !== undefined && id !== '')
    },

    takenLabel (field) {
      const taken = this.scopeTaken[field] || {}
      const names = Object.keys(taken).map(id => `${this.scopeEntityName(field, id)} (${taken[id]})`)
      return names.join(', ')
    },

    scopeEntityName (field, id) {
      // căutăm în listele nefiltrate, ca să avem numele și pentru cele ascunse
      const list = this.scopeAllOptions[this.scopeOptionKey(field)] || []
      const found = list.find(item => String(item.id) === String(id))
      return found ? found.name : `#${id}`
    },

    // delivery_ranges.location_id este TOKEN-ul locației
    locationNameByToken (token) {
      const found = this.availableLocations.find(loc => String(loc.token) === String(token))
      return found ? found.name : null
    },

    // Eticheta afișată: zonele primesc locația în paranteză,
    // iar legăturile rămase orfane își păstrează id-ul ca urmă
    scopeItemLabel (item) {
      if (item.missing) {
        return `#${item.id} (legătură ștearsă)`
      }

      let label = item.name

      if (item.location_id) {
        const locationName = this.locationNameByToken(item.location_id)
        label += ` (${locationName || item.location_id})`
      }

      if (item.deleted) {
        label += ' (șters)'
      }

      return label
    },

    onLocationsChanged () {
      this.loadScopeOptions()
    },

    loadScopeOptions () {
      const locationIds = this.selectedLocationIds

      if (!locationIds.length) {
        this.scopeOptions = { top_categories: [], delivery_ranges: [], customers_group: [] }
        this.scopeAllOptions = { top_categories: [], delivery_ranges: [], customers_group: [] }
        this.scopeTaken = { top_category_ids: {}, delivery_range_ids: {}, customers_group_ids: {} }
        this.plainProfileTaken = null
        return
      }

      this.loadingScopeOptions = true

      const data = {
        token: this.token,
        company_id: this.companyId,
        location_ids: locationIds
      }

      if (!this.isNew()) {
        data.price_profiles_id = this.route.params.id
      }

      this.apiRequest('/price-profiles/scope-options', 'GET', data, (response) => {
        this.loadingScopeOptions = false

        if (!response.status) {
          return
        }

        this.scopeTaken = response.taken || { top_category_ids: {}, delivery_range_ids: {}, customers_group_ids: {} }
        this.plainProfileTaken = response.plain_profile_taken || null

        // Ascundem din liste entitățile deja folosite de alt profil pe aceleași locații
        const options = response.data || {}
        const all = {}
        const filtered = {}

        this.scopeFields.forEach(field => {
          const key = this.scopeOptionKey(field)
          const taken = this.scopeTaken[field] || {}
          const selected = this.scopeIds(field).map(String)

          // eticheta include locația pentru zonele de livrare
          all[key] = (options[key] || []).map(item => Object.assign({}, item, {
            name: this.scopeItemLabel(item)
          }))

          filtered[key] = all[key].filter(item => {
            return !taken[item.id] || selected.indexOf(String(item.id)) !== -1
          })
        })

        Vue.set(this, 'scopeAllOptions', all)
        Vue.set(this, 'scopeOptions', filtered)
        this.pruneScopeSelections()
      })
    },

    // După schimbarea locațiilor, scoatem filtrele care nu mai sunt valide.
    // Legăturile orfane rămân pe loc, ca să nu pierdem urma lor.
    pruneScopeSelections () {
      this.scopeFields.forEach(field => {
        const key = this.scopeOptionKey(field)
        const availableIds = (this.scopeOptions[key] || []).map(item => String(item.id))

        const kept = (this.model[field] || []).filter(item => {
          if (item && typeof item === 'object' && (item.missing || item.deleted)) {
            return true
          }
          const id = (item && typeof item === 'object') ? item.id : item
          return availableIds.indexOf(String(id)) !== -1
        })

        Vue.set(this.model, field, kept)
      })
    },

    isNew () {
      return !this.route.params.id || this.route.params.id === 'add'
    },

    getData () {
      this.loading = true
      this.loadAvailableLocations()

      if (!this.isNew()) {
        this.loadProfile()
      } else {
        this.loading = false
      }
    },

    loadAvailableLocations () {
      let companyData = this.$session.get('selectedCompanyData')
      this.availableLocations = companyData.locations_details.map(l => ({
        token: l.token,
        true_id: l.true_id,
        name: l.name
      }))
    },

    getCategories () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/category', 'GET', data, (response) => {
        if (response.status === true) {
          this.mainCategories = response.data
        }
      })
    },

    loadProfile () {
      const data = {
        token: this.token,
        company_id: this.companyId
      }

      this.apiRequest('/price-profiles/' + this.route.params.id, 'GET', data, (response) => {

        if (response.status) {
          this.model = {
            id: response.data.id,
            name: response.data.name,
            location_ids: response.data.location_ids || [],
            top_category_ids: this.mapScopeItems(response.data.top_category_ids),
            delivery_range_ids: this.mapScopeItems(response.data.delivery_range_ids),
            customers_group_ids: this.mapScopeItems(response.data.customers_group_ids)
          }

          this.loadScopeOptions()
        }
        this.loadProducts(1)
        this.loading = false
      })
    },

    mapScopeItems (items) {
      return (items || []).map(item => ({
        id: item.id,
        name: this.scopeItemLabel(item),
        missing: !!item.missing,
        deleted: !!item.deleted
      }))
    },

    getVatOptions (locationIds = null) {
      var data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        location_ids: locationIds
      }
      this.apiRequest('/location/vat', 'GET', data, (response) => {
        if (response.status === true) {
          Vue.set(this, 'vatOptions', response.data || [])
        }
      })
    },

    debouncedLoadProducts () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.productsPagination.page = 1 
        this.loadProducts()
      }, 1300)
    },

    resetFilters () {
      this.filters = {
        name: '',
        product_id: '',
        category_id: ''
      }
      this.clearDiscountSelection()
      this.productsPagination.page = 1
      this.loadProducts()
    },

    async loadProducts (firstTime = false) {
      if (!firstTime) {
        await this.validate(null, 1);
      }
      this.loadingProducts = true

      // Golește pricingData și editModes la fiecare load (pentru paginare curată)
      this.pricingData = {}
      this.editModes = {}

      const data = {
        token: this.token,
        company_id: this.companyId,
        location_ids: this.model.location_ids,
        page: this.productsPagination.page,
        perPage: this.productsPagination.perPage
      }

      // Trimite price_profiles_id dacă există (edit mode)
      if (this.route.params.id && this.route.params.id !== 'add') {
        data.price_profiles_id = this.route.params.id
      }

      if (this.filters.name) data.name = this.filters.name
      if (this.filters.product_id) data.product_id = this.filters.product_id
      if (this.filters.category_id) data.category_id = this.filters.category_id

      this.apiRequest('/price-profiles/available-products', 'GET', data, (response) => {
        if (response.status) {
          this.products = response.data
          this.productsPagination = response.pagination || this.productsPagination
          // Folosește pricingData primită din API
          this.initializePricingData(response.pricingData || {})
        }
        this.loadingProducts = false
      })
    },

    initializePricingData (apiPricingData) {
      this.products.forEach(product => {
        this.sources.forEach(source => {
          source.methods.forEach(method => {
            const key = `${product.id}_${source.id}_${method.id}`
            const existing = apiPricingData[key]

            if (existing) {
              // Există în DB - folosește valorile salvate
              Vue.set(this.pricingData, key, {
                id: existing.id,
                price: existing.price,
                vat_id: existing.vat_id,
                vat_amount: existing.vat_amount,
                pos_id: existing.pos_id,
                active: existing.active,
                product_id: product.id,
                source: source.id,
                method: method.id,
                discounts: existing.discounts || []
              })
              // Setează editMode true pentru cele existente
              Vue.set(this.editModes, key, true)
            } else {
              // Nu există în DB - folosește default din produs
              Vue.set(this.pricingData, key, {
                id: null,
                price: product.price || '',
                vat_id: product.vat_id || '',
                vat_amount: product.vat_amount || '',
                pos_id: product.pos_id || '',
                active: product.active || false,
                product_id: product.id,
                source: source.id,
                method: method.id,
                discounts: []
              })
              // editMode rămâne false (default)
              Vue.set(this.editModes, key, false)
            }
          })
        })
      })
    },

    updateVatFromSelect (productId, sourceId, methodId, event) {
      const key = `${productId}_${sourceId}_${methodId}`
      const selectElement = event.target
      const selectedOption = selectElement.options[selectElement.selectedIndex]

      if (selectedOption && selectedOption.value !== 'null') {
        const vatId = selectedOption.value
        const vatAmount = selectedOption.dataset.amount

        Vue.set(this.pricingData[key], 'vat_id', vatId)
        Vue.set(this.pricingData[key], 'vat_amount', vatAmount)
      } else {
        Vue.set(this.pricingData[key], 'vat_id', '')
        Vue.set(this.pricingData[key], 'vat_amount', '')
      }
    },

    getPricingKey (productId, sourceId, methodId) {
      return `${productId}_${sourceId}_${methodId}`
    },

    getDiscounts (productId, sourceId, methodId) {
      const pricing = this.pricingData[this.getPricingKey(productId, sourceId, methodId)]
      return (pricing && pricing.discounts) ? pricing.discounts : []
    },

    hasDiscounts (productId, sourceId, methodId) {
      return this.getDiscounts(productId, sourceId, methodId).length > 0
    },

    getDiscountSum (productId, sourceId, methodId) {
      const sum = this.getDiscounts(productId, sourceId, methodId).reduce((total, line) => {
        return total + (parseFloat(line.discount) || 0)
      }, 0)
      return Math.round(sum * 100) / 100
    },

    getDiscountedPrice (productId, sourceId, methodId) {
      const discounts = this.getDiscounts(productId, sourceId, methodId)
      if (!discounts.length) return null
      if (discounts.length === 1) {
        return discounts[0].discounted_price
      }
      const pricing = this.pricingData[this.getPricingKey(productId, sourceId, methodId)]
      const basePrice = parseFloat(pricing && pricing.price) || 0
      const discounted = basePrice - this.getDiscountSum(productId, sourceId, methodId)
      return Math.round(discounted * 100) / 100
    },

    isDiscountCampaignActive (productId, sourceId, methodId) {
      const discounts = this.getDiscounts(productId, sourceId, methodId)
      return discounts.some(line => {
        if (!line.campaign) return true
        return line.campaign.active === true || parseInt(line.campaign.active, 10) === 1
      })
    },

    getDiscountTooltip (productId, sourceId, methodId) {
      const discounts = this.getDiscounts(productId, sourceId, methodId)
      if (!discounts.length) return ''
      const pricing = this.pricingData[this.getPricingKey(productId, sourceId, methodId)]
      const basePrice = parseFloat(pricing && pricing.price) || 0
      return discounts.map(line => {
        const name = (line.campaign && line.campaign.name) || ('#' + line.discount_campaign_id)
        const finalPrice = line.discounted_price != null
          ? line.discounted_price
          : Math.round((basePrice - (parseFloat(line.discount) || 0)) * 100) / 100
        const parts = [
          name,
          `${basePrice} → ${finalPrice} RON`
        ]
        if (line.number_of_units != null) {
          parts.push(`Limită ${line.number_of_units} · Vândute ${line.units_sold || 0}`)
        }
        return parts.join('\n')
      }).join('\n\n')
    },

    isDiscountCellSelected (productId, sourceId, methodId) {
      return !!this.discountSelection[this.getPricingKey(productId, sourceId, methodId)]
    },

    toggleDiscountCellSelection (product, source, method, checked) {
      const key = this.getPricingKey(product.id, source.id, method.id)
      const pricing = this.pricingData[key]
      if (!pricing) return
      if (checked) {
        Vue.set(this.discountSelection, key, {
          product_id: product.id,
          product_name: product.name,
          source: source.id,
          method: method.id,
          source_name: source.name,
          method_name: method.name,
          price_profile_product_id: pricing.id || null,
          has_discount: this.hasDiscounts(product.id, source.id, method.id),
          line_ids: this.getDiscounts(product.id, source.id, method.id).map(d => d.id)
        })
      } else {
        Vue.delete(this.discountSelection, key)
      }
    },

    clearDiscountSelection () {
      this.discountSelection = {}
    },

    openDiscountCampaignModal () {
      const selections = Object.values(this.discountSelection)
      const productMap = {}
      const comboMap = {}
      selections.forEach(sel => {
        productMap[sel.product_id] = {
          id: sel.product_id,
          label: sel.product_name || (`#${sel.product_id}`)
        }
        const comboKey = `${sel.source}_${sel.method}`
        comboMap[comboKey] = {
          source: sel.source,
          method: sel.method,
          label: `${sel.source_name} ${sel.method_name}`
        }
      })
      this.discountCampaignPrefill = {
        price_profile_id: parseInt(this.route.params.id, 10) || null,
        products: Object.values(productMap),
        product_ids: Object.keys(productMap).map(id => parseInt(id, 10)),
        source_method_combos: Object.values(comboMap)
      }
      this.showDiscountCampaignModal = true
    },

    closeDiscountCampaignModal () {
      this.showDiscountCampaignModal = false
      this.discountCampaignPrefill = null
    },

    submitDiscountCampaignModal () {
      const form = this.$refs.discountCampaignForm
      if (form && typeof form.validate === 'function') {
        form.validate()
      }
    },

    onDiscountCampaignSaved () {
      this.closeDiscountCampaignModal()
      this.clearDiscountSelection()
      this.loadProducts()
    },

    openDiscountLineModal (product, source, method) {
      const discounts = this.getDiscounts(product.id, source.id, method.id)
      if (!discounts.length) return
      const pricing = this.pricingData[this.getPricingKey(product.id, source.id, method.id)]
      this.discountLineContext = {
        productId: product.id,
        productName: product.name,
        sourceId: source.id,
        methodId: method.id,
        sourceName: source.name,
        methodName: method.name
      }
      this.discountLineEdits = discounts.map(line => {
        const basePrice = parseFloat(line.price != null ? line.price : (pricing && pricing.price)) || 0
        const discount = parseFloat(line.discount) || 0
        const campaign = line.campaign || {}
        let period = ''
        if (campaign.start_date || campaign.end_date) {
          period = [campaign.start_date, campaign.end_date].filter(Boolean).join(' – ')
        }
        return {
          id: line.id,
          campaignId: line.discount_campaign_id || campaign.id,
          campaignName: campaign.name || '',
          period,
          number_of_units: line.number_of_units,
          units_sold: line.units_sold || 0,
          basePrice,
          discount,
          discounted_price: line.discounted_price != null
            ? line.discounted_price
            : Math.round((basePrice - discount) * 100) / 100,
          error: ''
        }
      })
      this.showDiscountLineModal = true
    },

    closeDiscountLineModal () {
      this.showDiscountLineModal = false
      this.discountLineContext = null
      this.discountLineEdits = []
      this.discountLineSaving = false
    },

    recalcLineEdit (idx) {
      const line = this.discountLineEdits[idx]
      if (!line) return
      const discount = parseFloat(line.discount)
      if (isNaN(discount) || discount < 0) {
        line.error = this.$t('discountCampaigns.invalidDiscount')
        return
      }
      if (discount > line.basePrice) {
        line.error = this.$t('discountCampaigns.discountExceedsPrice')
        return
      }
      line.error = ''
      line.discounted_price = Math.round((line.basePrice - discount) * 100) / 100
    },

    async saveDiscountLineFromModal (lineEdit) {
      if (!this.discountLineContext || lineEdit.error) return
      this.discountLineSaving = true
      await this.saveDiscountLine(
        this.discountLineContext.productId,
        this.discountLineContext.sourceId,
        this.discountLineContext.methodId,
        lineEdit.id,
        lineEdit.discount,
        { silent: true }
      )
      this.discountLineSaving = false
      const discounts = this.getDiscounts(
        this.discountLineContext.productId,
        this.discountLineContext.sourceId,
        this.discountLineContext.methodId
      )
      if (!discounts.length) {
        this.closeDiscountLineModal()
      }
    },

    async saveAllDiscountLinesFromModal () {
      if (this.hasDiscountLineErrors || !this.discountLineEdits.length) return
      this.discountLineSaving = true
      for (const lineEdit of this.discountLineEdits) {
        await this.saveDiscountLine(
          this.discountLineContext.productId,
          this.discountLineContext.sourceId,
          this.discountLineContext.methodId,
          lineEdit.id,
          lineEdit.discount,
          { silent: true }
        )
      }
      this.discountLineSaving = false
      this.closeDiscountLineModal()
    },

    async deleteDiscountLinesFromModal () {
      const lines = this.discountLineEdits.slice()
      if (!lines.length) return
      const confirmed = await swal({
        text: this.$t('discountCampaigns.confirmDeleteLine'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel')
      }).catch(() => false)
      if (!confirmed) return

      this.discountLineSaving = true
      try {
        for (const lineEdit of lines) {
          const response = await this.apiRequestPromise(
            '/discount-campaigns/lines/' + lineEdit.id,
            'DELETE',
            {
              token: this.token,
              company_id: this.companyId
            }
          )
          if (!response.status) {
            throw new Error(response.message || this.$t('validations.wentWrong'))
          }
          const ctx = this.discountLineContext
          const key = this.getPricingKey(ctx.productId, ctx.sourceId, ctx.methodId)
          const pricing = this.pricingData[key]
          if (pricing && pricing.discounts) {
            Vue.set(pricing, 'discounts', pricing.discounts.filter(d => d.id !== lineEdit.id))
          }
        }
        this.closeDiscountLineModal()
      } catch (error) {
        swal({
          title: '',
          text: (error && error.message) || this.$t('validations.wentWrong'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
      }
      this.discountLineSaving = false
    },

    async deleteDiscountLineFromModal (lineEdit) {
      const confirmed = await swal({
        text: this.$t('discountCampaigns.confirmDeleteLine'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel')
      }).catch(() => false)
      if (!confirmed) return

      this.discountLineSaving = true
      try {
        const response = await this.apiRequestPromise(
          '/discount-campaigns/lines/' + lineEdit.id,
          'DELETE',
          {
            token: this.token,
            company_id: this.companyId
          }
        )
        if (!response.status) {
          throw new Error(response.message || this.$t('validations.wentWrong'))
        }

        const ctx = this.discountLineContext
        const key = this.getPricingKey(ctx.productId, ctx.sourceId, ctx.methodId)
        const pricing = this.pricingData[key]
        if (pricing && pricing.discounts) {
          const next = pricing.discounts.filter(d => d.id !== lineEdit.id)
          Vue.set(pricing, 'discounts', next)
        }

        this.discountLineEdits = this.discountLineEdits.filter(e => e.id !== lineEdit.id)
        if (!this.discountLineEdits.length) {
          this.closeDiscountLineModal()
        }
      } catch (error) {
        swal({
          title: '',
          text: (error && error.message) || this.$t('validations.wentWrong'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
      }
      this.discountLineSaving = false
    },

    onDiscountSumChange (productId, sourceId, methodId, event) {
      const discounts = this.getDiscounts(productId, sourceId, methodId)
      if (discounts.length !== 1) return
      this.saveDiscountLine(productId, sourceId, methodId, discounts[0].id, event.target.value)
    },

    onDiscountInputClick (product, source, method) {
      if (this.getDiscounts(product.id, source.id, method.id).length > 1) {
        this.openDiscountLineModal(product, source, method)
      }
    },

    flashDiscountSaved (key) {
      this.discountFlashKey = key
      clearTimeout(this.discountFlashTimer)
      this.discountFlashTimer = setTimeout(() => {
        this.discountFlashKey = null
      }, 1000)
    },

    async saveDiscountLine (productId, sourceId, methodId, lineId, rawValue, options = {}) {
      const key = this.getPricingKey(productId, sourceId, methodId)
      const pricing = this.pricingData[key]
      if (!pricing || !lineId) return

      const discountValue = parseFloat(rawValue)
      if (isNaN(discountValue) || discountValue < 0) {
        swal({
          title: '',
          text: this.$t('discountCampaigns.invalidDiscount'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
        return
      }

      const lineIndex = (pricing.discounts || []).findIndex(d => d.id === lineId)
      if (lineIndex === -1) return

      const line = pricing.discounts[lineIndex]
      const basePrice = parseFloat(line.price != null ? line.price : pricing.price) || 0
      if (discountValue > basePrice) {
        swal({
          title: '',
          text: this.$t('discountCampaigns.discountExceedsPrice'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
        return
      }
      const discountedPrice = Math.round((basePrice - discountValue) * 100) / 100

      const data = {
        token: this.token,
        company_id: this.companyId,
        discount: discountValue,
        discounted_price: discountedPrice
      }

      try {
        const response = await this.apiRequestPromise(
          '/discount-campaigns/lines/' + lineId,
          'PUT',
          data
        )

        const updated = (response.data && response.data.id)
          ? response.data
          : {
            ...line,
            discount: discountValue,
            discounted_price: discountedPrice
          }

        Vue.set(pricing.discounts, lineIndex, {
          ...line,
          ...updated
        })

        this.flashDiscountSaved(key)

        if (!options.silent) {
          // Compact feedback: border flash only
        }
      } catch (error) {
        swal({
          title: '',
          text: (error && error.message) || this.$t('validations.wentWrong'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
        this.$forceUpdate()
      }
    },

    getEditMode (productId, sourceId, methodId) {
      const key = `${productId}_${sourceId}_${methodId}`
      return this.editModes[key] || false
    },

    toggleEditMode (productId, sourceId, methodId, value) {
      const key = `${productId}_${sourceId}_${methodId}`
      Vue.set(this.editModes, key, value)

      if (value && this.pricingData[key]) {
        if (!this.pricingData[key].price) {
          const product = this.products.find(p => p.id === productId)
          if (product) {
            Vue.set(this.pricingData[key], 'price', product.price || '')
            Vue.set(this.pricingData[key], 'vat_id', product.vat_id || '')
            Vue.set(this.pricingData[key], 'vat_amount', product.vat_amount || '')
            Vue.set(this.pricingData[key], 'pos_id', product.pos_id || '')
          }
        }
      }
    },

    async changePage (newPage) {
      if (newPage < 1 || newPage > this.productsPagination.totalPages) return
      this.productsPagination.page = newPage
      this.loadProducts()
    },

    removeLocation (loc) {
      const idx = this.model.location_ids.findIndex(l => l.id === loc.id)
      if (idx !== -1) {
        this.model.location_ids.splice(idx, 1)
      }
    },

    async validate (e, noReload = null) {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        swal({
          title: '',
          text: this.$t('validations.checkErrors'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
        return
      }

      // Al doilea profil pe aceleași locații trebuie să aibă cel puțin un filtru
      if (this.plainProfileTaken && !this.hasAnyScope) {
        swal({
          title: '',
          text: `Există deja un profil doar pe locație pentru locațiile selectate: "${this.plainProfileTaken}". Alege o categorie de top, o zonă de livrare sau un grup de clienți.`,
          type: 'error',
          showConfirmButton: true
        })
        return
      }

      await this.saveProfile(null, noReload)
    },

    async saveProfile (e, noReload) {
      this.loading = true

      const productsToSave = []
      const productsToDelete = []

      // Parcurge toate cheile din editModes
      Object.keys(this.editModes).forEach(key => {
        const pricing = this.pricingData[key]
        if (!pricing) return

        if (this.editModes[key]) {
          // EditMode = true -> salvăm (create sau update)
          if (pricing.price !== '' || pricing.active || pricing.pos_id !== '') {
            const { discounts, ...productPayload } = pricing
            productsToSave.push(productPayload)
          }
        } else {
          // EditMode = false -> ștergem dacă există ID în DB
          if (pricing.id) {
            productsToDelete.push({
              id: pricing.id,
              product_id: pricing.product_id,
              source: pricing.source,
              method: pricing.method
            })
          }
        }
      })

      let data = {
        token: this.token,
        company_id: this.companyId,
        name: this.model.name,
        location_ids: this.selectedLocationIds,
        top_category_ids: this.scopeIds('top_category_ids'),
        delivery_range_ids: this.scopeIds('delivery_range_ids'),
        customers_group_ids: this.scopeIds('customers_group_ids'),
        products: productsToSave,
        products_to_delete: productsToDelete // Trimite și cele de șters
      }

      let response = null;
      if (this.isNew()) {
        response = await this.apiRequestPromise('/price-profiles', 'POST', data)
        this.handleResponse(response, 'creat', noReload, data)
      } else {
        response = await this.apiRequestPromise('/price-profiles/' + this.route.params.id, 'PUT', data)
        this.handleResponse(response, 'actualizat', noReload, data)
      }
    },

    async handleResponse (response, action, noReload, requestData) {
      this.loading = false

      if (response.status) {
        const entityId = (this.route.params && this.route.params.id) ||
          (response.data && (response.data.id || (response.data[0] && response.data[0].id))) ||
          null
        this.trackAdminEvent('ADMIN_PRICE_LIST_UPDATE', 'price_list', entityId, {
          price_list_id: entityId,
          name: (requestData && requestData.name) || (this.model && this.model.name) || null,
          request: requestData || null,
          response: response
        })
        swal({
          title: '',
          text: `Profil de preț ${action} cu succes!`,
          type: 'success',
          showConfirmButton: false,
          timer: 1500
        }).then(() => {

        })
        if (action == 'creat') {
          this.backButton()
        } else {
          this.$bus.$emit('refresh')
          if (!noReload) {
            this.refreshDataAfterSave()
          }
        }
      } else {
        console.log(response.errors)
        if (response.errors) {
          for (let field in response.errors) {
            this.errors.add(field, response.errors[field])
          }
        }

        swal({
          title: '',
          text: response.message || this.$t('validations.wentWrong'),
          type: 'error',
          showConfirmButton: false,
          timer: 2000
        })
      }
    },

    refreshDataAfterSave () {
      // Resetezi tot și reîncarci
      this.deletedKeys = new Set()
      this.editModes = {}
      this.pricingData = {}
      this.apiPricingData = {}
      this.allLoadedPricingIds = new Set()

      // Reîncarcă produsele cu date proaspete
      this.loadProducts(true)
    },

    handleDelete () {
      swal({
        title: 'Confirmare ștergere',
        text: `Ești sigur că vrei să ștergi profilul "${this.model.name}"?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no'),
        confirmButtonColor: '#f56c6c'
      }).then((result) => {
        if (result) {
          this.loading = true

          const data = {
            token: this.token,
            company_id: this.companyId
          }

          this.apiRequest('/price-profiles/' + this.route.params.id, 'DELETE', data, (response) => {
            this.loading = false

            if (response.status) {
              const id = this.route.params.id
              this.trackAdminEvent('ADMIN_PRICE_LIST_DELETED', 'price_list', id, {
                price_list_id: id,
                name: (this.model && this.model.name) || null
              })
              swal({
                title: '',
                text: 'Profil șters cu succes!',
                type: 'success',
                showConfirmButton: false,
                timer: 1500
              }).then(() => {

              })
              this.backButton()
            } else {
              swal({
                title: '',
                text: response.message || this.$t('validations.wentWrong'),
                type: 'error',
                showConfirmButton: false,
                timer: 2000
              })
            }
          })
        }
      })
    },

    goToProduct (prod) {
      return 0;
      // trebuie facut in produs ca daca nu exista celee de mai jos sa le incarce
      // sau puse in variablile globale!!
      this.$router.push({
        name: 'Edit Product',
        params: {
          id: prod.id,
          productData: prod,
          mainCategories: this.mainCategories,
          // taxes: this.taxes,
          // kdsSections: this.kdsSections,
          // productOptions: this.productOptions,
          // companyProductFilters: this.companyProductFilters,
          // measurementsUnits: this.measurementsUnits,
          // language: this.language,
          // languages: this.languages
        }
      })
    },

    backButton () {
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    },

    // ================== METODE APLICARE RAPIDĂ ==================

    openQuickApplyModal (product) {
      this.quickApplyProduct = product
      // Pre-completează cu valorile din produs (dacă există)
      this.quickApplyData = {
        price: product.price || '',
        vat_id: product.vat_id || null,
        vat_amount: product.vat_amount || '',
        pos_id: product.pos_id || '',
        active: true
      }
      this.showQuickApplyModal = true
    },

    closeQuickApplyModal () {
      this.showQuickApplyModal = false
      this.quickApplyProduct = null
      this.quickApplyData = {
        price: '',
        vat_id: null,
        vat_amount: '',
        pos_id: '',
        active: true
      }
    },

    updateQuickApplyVat (event) {
      const selectedOption = event.target.options[event.target.selectedIndex]
      if (selectedOption && selectedOption.value !== 'null') {
        const vat = this.vatOptions.find(v => v.id == this.quickApplyData.vat_id)
        if (vat) {
          this.quickApplyData.vat_amount = vat.amount
        }
      } else {
        this.quickApplyData.vat_amount = ''
      }
    },

    clearProductPricing () {
      if (!this.quickApplyProduct) return

      const product = this.quickApplyProduct

      swal({
        title: 'Confirmare ștergere',
        text: `Ștergi toate prețurile produsului "${product.name}" din acest profil?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no'),
        confirmButtonColor: '#f56c6c'
      }).then((result) => {
        if (!result) return

        // Dezactivează editMode pentru toate sursele/metodele produsului.
        // La salvare, cele cu id existent ajung în products_to_delete.
        this.sources.forEach(source => {
          source.methods.forEach(method => {
            const key = `${product.id}_${source.id}_${method.id}`
            Vue.set(this.editModes, key, false)
          })
        })

        this.closeQuickApplyModal()

        swal({
          title: '',
          text: 'Prețurile au fost marcate pentru ștergere. Salvează pentru a aplica!',
          type: 'success',
          showConfirmButton: false,
          timer: 2000
        })
      })
    },

    applyQuickApply () {
      if (!this.quickApplyProduct) return

      const productId = this.quickApplyProduct.id
      const data = this.quickApplyData

      // Parcurge toate sursele și metodele pentru acest produs
      this.sources.forEach(source => {
        source.methods.forEach(method => {
          const key = `${productId}_${source.id}_${method.id}`

          // Activează editMode pentru această celulă
          Vue.set(this.editModes, key, true)

          // Setează valorile în pricingData
          Vue.set(this.pricingData[key], 'price', data.price)
          Vue.set(this.pricingData[key], 'vat_id', data.vat_id || '')
          Vue.set(this.pricingData[key], 'vat_amount', data.vat_amount || '')
          Vue.set(this.pricingData[key], 'pos_id', data.pos_id)
          Vue.set(this.pricingData[key], 'active', data.active)
        })
      })

      // Închide modalul
      this.closeQuickApplyModal()

      // Afișează confirmare
      swal({
        title: '',
        text: 'Valorile au fost aplicate la toate sursele și metodele!',
        type: 'success',
        showConfirmButton: false,
        timer: 1500
      })
    }
  }
}
</script>

<style scoped>
/* Card pentru tabel */
.pricing-table-card {
  margin-top: 20px;
}

.pricing-table-card .card-header {
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
}

.pricing-table-card .card-header h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  font-weight: 600;
}

/* Filtre */
.filters-wrapper {
  padding: 15px 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 15px;
}

.filters-wrapper .form-group {
  margin-bottom: 0;
}

/* Container DOAR pentru scroll orizontal - fără scroll vertical */
.table-responsive-wrapper {
  position: relative;
  width: 100%;
  overflow: visible; /* Fără scroll aici */
}

/* Doar scroll orizontal, NU vertical */
.table-horizontal-scroll {
  overflow-x: auto;
  overflow-y: visible; /* Fără scroll vertical */
  max-width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

/* Tabel styling - fără max-height, se extinde natural */
.pricing-table {
  width: auto;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}

/* Fără sticky pe thead - headerul se comportă normal */
.pricing-table thead {
  position: static; /* Fără sticky */
}

.pricing-table th,
.pricing-table td {
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  vertical-align: top;
}

.pricing-table th:last-child,
.pricing-table td:last-child {
  border-right: none;
}

/* Coloana produs (sticky orizontal DOAR) */
.product-col {
  min-width: 200px;
  max-width: 250px;
  background-color: #fff;
  z-index: 10;
}

.sticky-col {
  position: sticky;
  left: 0;
  box-shadow: 2px 0 5px rgba(0,0,0,0.1);
  background-color: #fff;
}

/* Header coloană produs */
.pricing-table thead .product-col {
  background-color: #f5f7fa;
  z-index: 20; /* Deasupra celorlalte celule */
}

.product-info {
  padding: 10px;
}

.product-info strong {
  display: block;
  margin-bottom: 5px;
  color: #303133;
  cursor: pointer;
}

.product-info strong:hover {
  color: #409eff;
  text-decoration: underline;
}

.product-id {
  font-size: 11px;
  margin-bottom: 3px;
}

.standard-price {
  font-size: 12px;
  margin-bottom: 8px;
}

/* Buton aplicare rapidă */
.quick-apply-btn {
  margin-top: 8px;
  font-size: 11px;
  padding: 4px 8px;
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: #fff;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-apply-btn:hover {
  background-color: #138496;
  border-color: #117a8b;
}

.quick-apply-btn i {
  margin-right: 4px;
}

/* Coloane sursă+metodă */
.source-method-col {
  min-width: 160px;
  max-width: 160px;
  text-align: center;
  padding: 8px 4px !important;
  background-color: #f5f7fa;
}

.source-header {
  font-weight: 600;
  color: #409eff;
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.method-header {
  font-size: 11px;
  color: #606266;
}

/* Celule pricing */
.pricing-cell {
  min-width: 170px;
  max-width: 170px;
  padding: 8px !important;
  background-color: #fff;
}

.pricing-cell:hover {
  background-color: #f5f7fa;
}

.pricing-fields {
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: opacity 0.2s ease;
}

.pricing-fields.disabled {
  opacity: 0.4;
  pointer-events: none;
  filter: grayscale(0.8);
}

.field-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-row label {
  font-size: 10px;
  color: #909399;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0;
}

.input-sm {
  height: 28px;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 3px;
  border: 1px solid #dcdfe6;
}

.input-sm:focus {
  border-color: #409eff;
  outline: none;
}

.checkbox-row {
  flex-direction: row !important;
  align-items: center;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed #ebeef5;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 12px !important;
  color: #606266 !important;
  text-transform: none !important;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.discount-row-compact {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #ebeef5;
  min-height: 26px;
}

.discount-row-compact.is-selected {
  background: #f0f7ff;
  margin-left: -4px;
  margin-right: -4px;
  padding-left: 4px;
  padding-right: 4px;
  border-left: 2px solid #409eff;
}

.discount-select-check {
  width: 13px;
  height: 13px;
  margin: 0;
  flex-shrink: 0;
  accent-color: #409eff;
  cursor: pointer;
}

.discount-select-check:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.discount-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #67c23a;
  flex-shrink: 0;
}

.discount-dot.is-inactive {
  background: #e6a23c;
}

.discount-input-compact {
  width: 58px;
  height: 24px !important;
  padding: 2px 4px !important;
  font-size: 11px !important;
  border-color: #dcdfe6;
  color: #606266;
  font-weight: 400;
}

.discount-input-compact.is-saved-flash {
  border-color: #67c23a !important;
  transition: border-color 0.2s;
}

.discount-input-compact[readonly] {
  cursor: pointer;
  background: #f5f7fa;
}

.discount-count {
  font-size: 9px;
  color: #909399;
  line-height: 1;
}

.discount-edit-btn {
  width: 24px;
  height: 24px;
  padding: 0 !important;
  line-height: 22px;
  flex-shrink: 0;
}

.discount-empty {
  font-size: 11px;
  color: #c0c4cc;
  text-transform: none;
}

.discount-selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #f0f7ff;
  border: 1px solid #409eff;
  border-radius: 4px;
  font-size: 12px;
  color: #303133;
}

.discount-selection-bar__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.discount-campaign-modal {
  width: 92vw;
  max-width: 1100px;
  margin: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.3s ease;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.discount-campaign-modal .modal-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 88vh;
  overflow: hidden;
  border-radius: 8px;
}

.discount-campaign-modal .modal-header {
  flex: 0 0 auto;
}

.discount-campaign-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 10px 20px;
}

.discount-campaign-modal .modal-footer {
  flex: 0 0 auto;
}

.discount-line-modal {
  width: 92vw;
  max-width: 480px;
  margin: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.3s ease;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.discount-line-modal .modal-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 88vh;
  overflow: hidden;
  border-radius: 8px;
}

.discount-line-modal .modal-header {
  flex: 0 0 auto;
}

.discount-line-modal .modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.discount-line-editor__meta {
  margin-bottom: 12px;
}

.discount-line-editor .meta-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  margin-bottom: 4px;
}

.discount-line-editor .meta-label {
  color: #909399;
  font-size: 11px;
}

.discount-line-editor .meta-value {
  color: #303133;
  text-align: right;
}

.discount-line-modal .modal-footer {
  flex: 0 0 auto;
}

/* Paginare */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
  background-color: #f5f7fa;
}

.pagination-info {
  font-size: 13px;
  color: #606266;
}

.pagination-controls {
  display: flex;
  gap: 10px;
}

.pagination-controls .btn {
  padding: 6px 12px;
  font-size: 13px;
}

/* Loading animation */
.rotate {
  animation: rotate 1s linear infinite;
  display: inline-block;
}

@keyframes rotate {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}

/* Row alternativ pentru lizibilitate */
.pricing-table tbody tr:nth-child(even) td:not(.sticky-col) {
  background-color: #fafafa;
}

.pricing-table tbody tr:hover td:not(.sticky-col) {
  background-color: #f5f7fa;
}

/* ================== MODAL APLICARE RAPIDĂ ================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-apply-modal {
  width: 100%;
  max-width: 450px;
  margin: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-content {
  border-radius: 8px;
  overflow: hidden;
}

.modal-header {
  padding: 20px 20px 10px 20px;
  border-bottom: 1px solid #ebeef5;
  position: relative;
}

.modal-header .modal-title {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.modal-header .modal-title i {
  color: #17a2b8;
  margin-right: 8px;
}

.modal-subtitle {
  margin: 0;
  font-size: 13px;
  color: #606266;
}

.modal-subtitle strong {
  color: #409eff;
}

.modal-header .close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 18px;
  color: #909399;
  cursor: pointer;
  padding: 5px;
}

.modal-header .close:hover {
  color: #f56c6c;
}

.modal-body {
  padding: 20px;
}

.modal-description {
  margin: 0 0 15px 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}

.modal-description strong {
  color: #409eff;
}

.quick-apply-form .form-group {
  margin-bottom: 15px;
}

.quick-apply-form .form-group label {
  display: block;
  margin-bottom: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.quick-apply-form .form-group .form-control {
  width: 100%;
  height: 36px;
  padding: 6px 12px;
  font-size: 14px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
  transition: border-color 0.2s;
}

.quick-apply-form .form-group .form-control:focus {
  border-color: #409eff;
  outline: none;
}

.quick-clear-btn {
  width: 100%;
  padding: 8px 16px;
  font-size: 14px;
  background-color: #f56c6c;
  border: 1px solid #f56c6c;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-clear-btn:hover {
  background-color: #f78989;
  border-color: #f78989;
}

.quick-clear-btn i {
  margin-right: 5px;
}

.quick-apply-form .quick-clear-btn + .help-block {
  margin-top: 6px;
  font-size: 12px;
}

.quick-apply-form .checkbox-group {
  margin-bottom: 0;
}

.quick-apply-form .checkbox-group .checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #303133;
}

.quick-apply-form .checkbox-group .checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background-color: #f5f7fa;
}

/* Responsive */
@media (max-width: 768px) {
  .product-col {
    min-width: 150px;
    max-width: 150px;
  }

  .source-method-col,
  .pricing-cell {
    min-width: 140px;
    max-width: 140px;
  }

  .filters-wrapper .col-md-4,
  .filters-wrapper .col-md-3,
  .filters-wrapper .col-md-2 {
    margin-bottom: 10px;
  }

  .quick-apply-modal {
    margin: 10px;
    max-width: 100%;
  }
}
</style>

<!-- Unscoped: layout only — visuals come from global a.delete / button.save -->
<style>
.pp-modal-footer {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 10px !important;
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
  flex: 0 0 auto !important;
  background: #fff !important;
  margin: 0 !important;
}

.pp-modal-footer--split {
  justify-content: space-between !important;
}

.pp-modal-footer__actions {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0 !important;
  margin: 0 !important;
}

.pp-modal-footer .delete,
.pp-modal-footer .save {
  flex: 0 0 auto !important;
  float: none !important;
  width: auto !important;
  max-width: none !important;
}

.pp-modal-footer .delete.disabled,
.pp-modal-footer .save:disabled {
  opacity: 0.55 !important;
  pointer-events: none !important;
  cursor: not-allowed !important;
}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/Products/PriceProfileForm.vue